import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { X, Loader2, Users, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LevelBadge from "@/components/LevelBadge";

/**
 * Modal that lists followers or following of a given @username.
 *
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   username   — string, whose list to show
 *   mode       — "followers" | "following"
 */
export default function FollowListDialog({ open, onClose, username, mode }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null); // user_id being toggled

  useEffect(() => {
    if (!open || !username) return;
    setLoading(true);
    api
      .get(`/users/${username}/${mode}`)
      .then((r) => setRows(r.data?.users || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, username, mode]);

  const toggleFollow = async (row) => {
    if (!user) {
      toast.error("Sign in to follow");
      return;
    }
    if (row.is_self) return;
    setPendingId(row.user_id);
    try {
      if (row.following) {
        await api.delete(`/users/${row.username}/follow`);
        setRows((rs) =>
          rs.map((r) =>
            r.user_id === row.user_id ? { ...r, following: false } : r
          )
        );
      } else {
        await api.post(`/users/${row.username}/follow`);
        setRows((rs) =>
          rs.map((r) =>
            r.user_id === row.user_id ? { ...r, following: true } : r
          )
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    } finally {
      setPendingId(null);
    }
  };

  if (!open) return null;

  const title = mode === "followers" ? "followers" : "following";

  return (
    <div
      data-testid="follow-list-dialog"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0A0A0C] border border-[#00FFFF]/40 shadow-[0_0_60px_rgba(0,255,255,0.3)] relative max-h-[80vh] flex flex-col"
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0C] z-10">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#00FFFF]" strokeWidth={2} />
            <span
              className="font-display font-black text-lg tracking-tight uppercase glitch"
              data-text={title}
              style={{ textShadow: "0 0 10px rgba(0,255,255,0.5)" }}
            >
              {title}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              // @{username}
            </span>
          </div>
          <button
            data-testid="follow-list-close"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-5 w-5 text-[#00FFFF] animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center px-6">
              <Users className="h-10 w-10 mx-auto text-white/20 mb-3" strokeWidth={1.2} />
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                {mode === "followers"
                  ? "no followers yet"
                  : "not following anyone yet"}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {rows.map((r) => (
                <li
                  key={r.user_id}
                  data-testid={`follow-list-row-${r.username}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.02]"
                >
                  <Link
                    to={`/u/${r.username}`}
                    onClick={onClose}
                    className="flex items-center gap-3 flex-1 min-w-0 group"
                  >
                    {r.picture ? (
                      <img
                        src={picUrl(r.picture)}
                        alt=""
                        className="h-11 w-11 object-cover border border-white/10"
                      />
                    ) : (
                      <div className="h-11 w-11 flex items-center justify-center bg-[#FF007F]/15 text-[#FF007F] font-display font-black text-lg border border-[#FF007F]/30">
                        {(r.name || r.username || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-semibold truncate group-hover:text-[#00FFFF] transition-colors">
                          {r.name || r.username}
                        </p>
                        {r.gifter?.level > 0 && (
                          <LevelBadge level={r.gifter.level} kind="gifter" size="sm" />
                        )}
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 truncate">
                        @{r.username}
                      </p>
                    </div>
                  </Link>
                  {!r.is_self && user && (
                    <button
                      data-testid={`follow-toggle-${r.username}`}
                      onClick={() => toggleFollow(r)}
                      disabled={pendingId === r.user_id}
                      className={`px-3 py-1.5 border transition-all text-[10px] uppercase font-bold tracking-widest flex items-center gap-1.5 ${
                        r.following
                          ? "border-white/20 text-white/70 hover:border-[#FF007F] hover:text-[#FF007F]"
                          : "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14] hover:bg-[#39FF14]/20"
                      } disabled:opacity-40`}
                    >
                      {pendingId === r.user_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : r.following ? (
                        <>
                          <UserCheck className="h-3 w-3" strokeWidth={2.2} />
                          following
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-3 w-3" strokeWidth={2.2} />
                          follow
                        </>
                      )}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
